import React from 'react';
import { Calendar, dateFnsLocalizer } from 'react-big-calendar';
import format from 'date-fns/format';
import parse from 'date-fns/parse';
import startOfWeek from 'date-fns/startOfWeek';
import getDay from 'date-fns/getDay';
import es from 'date-fns/locale/es';
import 'react-big-calendar/lib/css/react-big-calendar.css';
import { useNavigate } from 'react-router-dom';
import { Box, Paper, Typography, alpha } from '@mui/material';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';

// Paleta profesional verde-azul
const COLORS = {
  gradient1: '#00b894',
  gradient2: '#0984e3',
  primary: '#0984e3',
  success: '#00b894',
  warning: '#eb9800ff',
  error: '#d63031',
  info: '#74b9ff',
  purple: '#6c5ce7',
  dark: '#2d3436'
};

const locales = {
  'es': es,
}

const localizer = dateFnsLocalizer({
  format,
  parse,
  startOfWeek: () => startOfWeek(new Date(), { weekStartsOn: 1 }),
  getDay,
  locales,
})

const MyCalendar = ({ events = [], deadlines = [] }) => {
  const navigate = useNavigate();

  const allEvents = [
    ...(Array.isArray(events) ? events : []),
    ...(Array.isArray(deadlines) ? deadlines.map(deadline => ({
      title: `Fecha límite: ${deadline.courseTitle}`,
      start: new Date(deadline.date),
      end: new Date(deadline.date),
      allDay: true,
      resource: 'deadline',
      courseId: deadline.courseId
    })) : [])
  ];

  const eventStyleGetter = (event) => {
    if (event.resource === 'deadline') {
      return {
        style: {
          background: `linear-gradient(135deg, ${COLORS.error} 0%, ${COLORS.orange || '#e17055'} 100%)`,
          color: 'white',
          borderRadius: '8px',
          border: 'none',
          cursor: 'pointer',
          padding: '2px 8px',
          fontWeight: 600,
          fontSize: '0.8rem',
          boxShadow: `0 2px 8px ${alpha(COLORS.error, 0.3)}`
        }
      };
    }
    // Eventos normales
    return {
      style: {
        background: `linear-gradient(135deg, ${COLORS.gradient1} 0%, ${COLORS.gradient2} 100%)`,
        color: 'white',
        borderRadius: '8px',
        border: 'none',
        cursor: 'pointer',
        padding: '2px 8px',
        fontWeight: 500,
        fontSize: '0.8rem',
        boxShadow: `0 2px 8px ${alpha(COLORS.primary, 0.3)}`
      }
    };
  };

  const handleEventClick = (event) => {
    if (event.resource === 'deadline' && event.courseId) {
      navigate(`/course/${event.courseId}/view`);
    }
  };

  return (
    <Paper
      elevation={0}
      sx={{
        borderRadius: 3,
        border: '2px solid #e0e0e0',
        overflow: 'hidden'
      }}
    >
      {/* Header */}
      <Box
        sx={{
          p: 2,
          background: `linear-gradient(135deg, ${COLORS.gradient1} 0%, ${COLORS.gradient2} 100%)`,
          display: 'flex',
          alignItems: 'center',
          gap: 1.5
        }}
      >
        <CalendarTodayIcon sx={{ color: 'white' }} />
        <Typography variant="h6" sx={{ color: 'white', fontWeight: 700 }}>
          Calendario de Formación
        </Typography>
      </Box>

      {/* Calendario */}
      <Box 
        sx={{ 
          height: 500,
          p: 2,
          '& .rbc-calendar': {
            fontFamily: 'inherit'
          },
          '& .rbc-header': {
            padding: '10px',
            fontWeight: 700,
            color: COLORS.dark,
            backgroundColor: alpha(COLORS.primary, 0.08),
            borderBottom: `2px solid ${alpha(COLORS.primary, 0.2)}`
          },
          '& .rbc-today': {
            backgroundColor: alpha(COLORS.success, 0.1)
          },
          '& .rbc-off-range-bg': {
            backgroundColor: '#f8f9fa'
          },
          '& .rbc-toolbar': {
            marginBottom: '16px',
            '& button': {
              color: COLORS.dark,
              fontWeight: 600,
              borderRadius: '8px',
              border: `1px solid ${alpha(COLORS.primary, 0.3)}`,
              '&:hover': {
                backgroundColor: alpha(COLORS.primary, 0.1),
                borderColor: COLORS.primary
              },
              '&.rbc-active': {
                background: `linear-gradient(135deg, ${COLORS.gradient1} 0%, ${COLORS.gradient2} 100%)`,
                color: 'white',
                borderColor: 'transparent'
              }
            }
          },
          '& .rbc-toolbar-label': {
            fontWeight: 700,
            fontSize: '1.1rem',
            color: COLORS.dark
          },
          '& .rbc-month-view': {
            border: `1px solid ${alpha(COLORS.primary, 0.2)}`,
            borderRadius: '8px',
            overflow: 'hidden'
          },
          '& .rbc-day-bg + .rbc-day-bg': {
            borderLeft: `1px solid ${alpha(COLORS.primary, 0.1)}`
          },
          '& .rbc-month-row + .rbc-month-row': {
            borderTop: `1px solid ${alpha(COLORS.primary, 0.1)}`
          },
          '& .rbc-date-cell': {
            padding: '4px 8px',
            fontWeight: 500,
            '&.rbc-now': {
              fontWeight: 700,
              color: COLORS.primary
            }
          },
          '& .rbc-event': {
            marginBottom: '2px'
          }
        }}
      >
        <Calendar
          localizer={localizer}
          events={allEvents}
          startAccessor="start"
          endAccessor="end"
          style={{ height: '100%' }}
          culture='es'
          messages={{
            next: "Siguiente",
            previous: "Anterior",
            today: "Hoy",
            month: "Mes",
            week: "Semana",
            day: "Día"
          }}
          eventPropGetter={eventStyleGetter}
          onSelectEvent={handleEventClick}
        />
      </Box>
    </Paper>
  )
}

export default MyCalendar;